/**
 * Reusable list controls for the Aether dashboard — search + sort (+ optional
 * status filter) over a plain array, re-skinned to the Aether glass look.
 *
 * Ported in spirit from the Kundenportal's `OrdersView` filter/sort pattern:
 *  - one cheap `useMemo` does filter + sort together
 *  - dates sorted by ISO-string `localeCompare` (no Date allocation, nulls sink)
 *  - the sort choice persists per list to localStorage; query + status filter
 *    live in component state (reset on reload)
 *  - status filter options are DERIVED from the data (only statuses that occur)
 *
 * Usage:
 *   const ctl = useListControls({
 *     items: orders,
 *     search: (o) => `${o.product_name} ${o.order_number}`,
 *     sorts: { newest: { label: 'Neueste', cmp: (a,b) => ... }, ... },
 *     defaultSort: 'newest',
 *     status: (o) => o.status,          // optional
 *     storageKey: 'aether_cust_orders', // optional (persists sort)
 *   });
 *   <ListToolbar controls={ctl} placeholder="Bestellungen durchsuchen …" />
 *   {ctl.result.map(...)}
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Search, ArrowUpDown, X } from 'lucide-react';

/* --- status → tone -------------------------------------------------------
 * Fuzzy, order-insensitive mapping of the many order/license status strings
 * the shop uses to a small set of colour tones, falling back to neutral grey
 * so an unknown status never crashes or goes hot-pink. */
const TONES = {
  emerald: { dot: 'bg-emerald-400', text: 'text-emerald-300', bg: 'bg-emerald-400/10' },
  amber:   { dot: 'bg-amber-400',   text: 'text-amber-300',   bg: 'bg-amber-400/10' },
  sky:     { dot: 'bg-sky-400',     text: 'text-sky-300',     bg: 'bg-sky-400/10' },
  red:     { dot: 'bg-red-400',     text: 'text-red-300',     bg: 'bg-red-400/10' },
  neutral: { dot: 'bg-white/40',    text: 'text-white/50',    bg: 'bg-white/5' },
};
const STATUS_TONE = [
  { tone: 'emerald', match: ['active', 'aktiv', 'paid', 'bezahlt', 'complete', 'granted', 'freigeschaltet'] },
  { tone: 'sky',     match: ['free', 'kostenlos', 'pending', 'ausstehend', 'offen'] },
  { tone: 'amber',   match: ['deactivated', 'deaktiviert', 'inactive', 'inaktiv', 'trial', 'testphase', 'refunded'] },
  { tone: 'red',     match: ['failed', 'fehlgeschlagen', 'canceled', 'cancelled', 'storniert', 'error'] },
];
export function toneForStatus(status) {
  const s = String(status || '').toLowerCase();
  for (const { tone, match } of STATUS_TONE) {
    if (match.some((m) => s.includes(m))) return TONES[tone];
  }
  return TONES.neutral;
}

export function StatusPill({ status, label }) {
  const t = toneForStatus(status);
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[9px] font-mono uppercase tracking-widest ${t.bg} ${t.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} />
      {label || status}
    </span>
  );
}

export function useListControls({ items = [], search, sorts = {}, defaultSort, status, storageKey }) {
  const sortKeys = Object.keys(sorts);
  const [sort, setSortRaw] = useState(() => {
    try {
      const stored = storageKey && localStorage.getItem(`${storageKey}_sort`);
      if (stored && sortKeys.includes(stored)) return stored;
    } catch {}
    return defaultSort || sortKeys[0];
  });
  const setSort = (v) => {
    setSortRaw(v);
    try { if (storageKey) localStorage.setItem(`${storageKey}_sort`, v); } catch {}
  };

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // The active status filter may disappear if the data changes (e.g. the last
  // deactivated key is removed) — drop it so we don't show an empty list.
  const statuses = useMemo(() => {
    if (!status) return [];
    const seen = [];
    for (const it of items) {
      const s = status(it);
      if (s && !seen.includes(s)) seen.push(s);
    }
    return seen;
  }, [items, status]);
  useEffect(() => {
    if (statusFilter && !statuses.includes(statusFilter)) setStatusFilter('');
  }, [statuses, statusFilter]);

  const result = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = items.filter((it) => {
      if (statusFilter && status && status(it) !== statusFilter) return false;
      if (q && search) return search(it).toLowerCase().includes(q);
      return true;
    });
    const cmp = sorts[sort] && sorts[sort].cmp;
    if (cmp) list = [...list].sort(cmp);
    return list;
  }, [items, query, statusFilter, sort, sorts, search, status]);

  return {
    query, setQuery, sort, setSort, sorts, sortKeys,
    statusFilter, setStatusFilter, statuses, result, total: items.length,
  };
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`px-2.5 py-1 rounded-full text-[9px] font-mono uppercase tracking-widest transition ${
        active ? 'bg-white/15 text-white' : 'bg-white/5 text-white/40 hover:text-white/70'
      }`}
    >
      {children}
    </button>
  );
}

export function ListToolbar({ controls, placeholder = 'Suchen …', statusLabels }) {
  const { query, setQuery, sort, setSort, sorts, sortKeys, statuses, statusFilter, setStatusFilter } = controls;
  return (
    <div className="flex flex-col gap-2 mb-3">
      <div className="flex items-center gap-2 flex-wrap">
        {/* search */}
        <div className="relative flex-1 min-w-[180px]">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg pl-8 pr-8 py-2 text-[12px] text-white/80 placeholder:text-white/25 outline-none focus:border-white/25"
          />
          {query && (
            <button onClick={() => setQuery('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70" title="Suche leeren">
              <X size={13} />
            </button>
          )}
        </div>
        {/* sort */}
        {sortKeys.length > 1 && (
          <div className="relative">
            <ArrowUpDown size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white/30 pointer-events-none" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="appearance-none rounded-lg border border-white/10 pl-7 pr-7 py-2 text-[11px] font-mono uppercase tracking-widest text-white/60 outline-none focus:border-white/25 cursor-pointer"
              style={{ backgroundColor: '#0a0a0a' }}
              title="Sortieren"
            >
              {sortKeys.map((k) => (
                <option key={k} value={k} style={{ background: '#0a0a0a' }}>{sorts[k].label}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      {/* status filter pills — only when more than one status actually occurs */}
      {statuses.length > 1 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <FilterChip active={!statusFilter} onClick={() => setStatusFilter('')}>Alle</FilterChip>
          {statuses.map((s) => (
            <FilterChip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)}>
              {(statusLabels && statusLabels[s]) || s}
            </FilterChip>
          ))}
        </div>
      )}
    </div>
  );
}
